import { useState, useEffect, useRef, RefObject } from 'react'

interface Props {
  src?: string
  alt?: string
  className?: string
  rootRef?: RefObject<HTMLElement>
  placeholderSrc?: string
}

export const LazyImage = (props: Props) => {
  const { src, alt = '', className, rootRef, placeholderSrc } = props
  const [isVisible, setIsVisible] = useState(false)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null)
  const tmpImgRef = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    setLoadedSrc('')

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)
      },
      { threshold: 0.1, root: rootRef?.current },
    )

    if (imgRef.current) {
      observerRef.current.observe(imgRef.current)
    }

    return () => {
      observerRef.current?.disconnect()
    }
  }, [rootRef, src])

  useEffect(() => {
    if (loadedSrc && observerRef.current) {
      observerRef.current?.disconnect()
    }
  }, [loadedSrc])

  useEffect(() => {
    if (loadedSrc || !isVisible) return

    tmpImgRef.current = tmpImgRef.current || new Image()
    tmpImgRef.current.src = src || ''

    const onLoad = () => setLoadedSrc(src || null)

    tmpImgRef.current.addEventListener('load', onLoad)

    return () => {
      tmpImgRef.current?.removeEventListener('load', onLoad)
      if (tmpImgRef.current) {
        tmpImgRef.current.src = ''
      }
    }
  }, [isVisible, loadedSrc, src])

  return (
    <img
      ref={imgRef}
      src={loadedSrc || placeholderSrc || undefined}
      alt={alt}
      className={className}
      loading="lazy"
    />
  )
}
